"use client";

import React, { useState, useCallback } from "react";
import Link from "next/link";
import { cn } from "@/utils/utils";

const PaginationContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn("flex items-center justify-center flex-wrap gap-y-2", className)}
      {...props}
    />
  );
});
PaginationContent.displayName = "PaginationContent";

const PaginationLink = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => {
  return (
    <button
      ref={ref}
      className={cn(
        "flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 data-[active=true]:bg-gray-100 data-[active=true]:text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100 dark:focus:ring-gray-700 dark:data-[active=true]:bg-gray-700 dark:data-[active=true]:text-gray-100",
        className
      )}
      {...props}
    />
  );
});
PaginationLink.displayName = "PaginationLink";

const PaginationEllipsis = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn(
        "h-9 w-9 items-center justify-center rounded-md text-sm font-medium",
        className
      )}
      {...props}
    >
      ...
    </span>
  );
});
PaginationEllipsis.displayName = "PaginationEllipsis";

const PaginationPrevious = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<typeof Link>
>(({ className, ...props }, ref) => {
  return (
    <Link
      ref={ref}
      className={cn(
        "flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100 dark:focus:ring-gray-700",
        className
      )}
      prefetch={false}
      {...props}
    />
  );
});
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNext = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<typeof Link>
>(({ className, ...props }, ref) => {
  return (
    <Link
      ref={ref}
      className={cn(
        "flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100 dark:focus:ring-gray-700",
        className
      )}
      prefetch={false}
      {...props}
    />
  );
});
PaginationNext.displayName = "PaginationNext";

const PaginationItem = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => {
  return (
    <button
      ref={ref}
      className={cn(
        "flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 data-[active=true]:bg-gray-100 data-[active=true]:text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100 dark:focus:ring-gray-700 dark:data-[active=true]:bg-gray-700 dark:data-[active=true]:text-gray-100",
        className
      )}
      {...props}
    />
  );
});
PaginationItem.displayName = "PaginationItem";

interface CustomPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => Promise<void>;
  showEllipsis?: boolean;
  maxVisiblePages?: number;
  className?: string;
  isLoading?: boolean;
}

const CustomPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  // showEllipsis = true,
  // maxVisiblePages = 5,
  className,
  isLoading = false,
}: CustomPaginationProps) => {
  const [isChanging, setIsChanging] = useState(false);
  const [pendingPage, setPendingPage] = useState<number | null>(null);

  // Reset states when loading completes
  React.useEffect(() => {
    if (!isLoading && pendingPage === currentPage) {
      setPendingPage(null);
      setIsChanging(false);
    }
  }, [isLoading, currentPage, pendingPage]);

  const handlePageChange = useCallback(async (newPage: number) => {
    if (isChanging || newPage === currentPage || newPage === pendingPage || isLoading) {
      return;
    }

    try {
      setIsChanging(true);
      setPendingPage(newPage);
      await onPageChange(newPage);
    } catch (error) {
      console.error('Error changing page:', error);
      setPendingPage(null);
    } finally {
    }
  }, [currentPage, isChanging, onPageChange, pendingPage, isLoading]);

  const handlePrevious = useCallback(() => {
    if (currentPage > 1 && !isChanging) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, handlePageChange, isChanging]);

  const handleNext = useCallback(() => {
    if (currentPage < totalPages && !isChanging) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, handlePageChange, isChanging, totalPages]);

  // Update the button rendering to show loading state
  const getButtonClassName = useCallback((pageNum: number) => {
    return cn(
      "flex h-10 w-10 items-center justify-center rounded-md text-sm font-medium transition-colors",
      currentPage === pageNum
        ? "bg-black text-white"
        : pendingPage === pageNum
        ? "bg-gray-200 text-gray-900"
        : "bg-gray-100 text-gray-600 hover:bg-gray-200",
      (isChanging || isLoading) && "opacity-50 cursor-not-allowed"
    );
  }, [currentPage, isChanging, isLoading, pendingPage]);

  // Update renderPageNumbers to use loading state
  const renderPageNumbers = useCallback(() => {
    const pageNumbers = [];
    const isDisabled = isChanging || isLoading;

    // Always show first page
    pageNumbers.push(
      <button
        key={1}
        onClick={() => handlePageChange(1)}
        disabled={isDisabled || pendingPage === 1}
        className={getButtonClassName(1)}
        aria-current={currentPage === 1 ? "page" : undefined}
       aria-label="1">
        1
      </button>
    );

    // Logic for showing ellipsis and page numbers
    if (totalPages <= 7) {
      // If we have 7 or fewer pages, show all pages without ellipsis
      for (let i = 2; i < totalPages; i++) {
        pageNumbers.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            disabled={isDisabled || pendingPage === i}
            className={getButtonClassName(i)}
            aria-current={currentPage === i ? "page" : undefined}
           aria-label={`Go to page ${i}`}>
            {i}
          </button>
        );
      }
    } else {
      // We have more than 7 pages, need to use ellipsis

      // Case 1: Current page is close to the beginning
      if (currentPage < 5) {
        for (let i = 2; i <= 5; i++) {
          pageNumbers.push(
            <button
              key={i}
              onClick={() => handlePageChange(i)}
              disabled={isDisabled || pendingPage === i}
              className={getButtonClassName(i)}
              aria-current={currentPage === i ? "page" : undefined}
               aria-label={`Go to page ${i}`}>
              {i}
            </button>
          );
        }
        pageNumbers.push(
          <span
            key="ellipsis1"
            className="flex h-10 w-10 items-center justify-center"
          >
            ...
          </span>
        );
      }
      // Case 2: Current page is close to the end
      else if (currentPage > totalPages - 4) {
        pageNumbers.push(
          <span
            key="ellipsis1"
            className="flex h-10 w-10 items-center justify-center"
          >
            ...
          </span>
        );
        for (let i = totalPages - 4; i < totalPages; i++) {
          pageNumbers.push(
            <button
              key={i}
              onClick={() => handlePageChange(i)}
              disabled={isDisabled || pendingPage === i}
              className={getButtonClassName(i)}
              aria-current={currentPage === i ? "page" : undefined}
             aria-label={`Go to page ${i}`}>
              {i}
            </button>
          );
        }
      }
      // Case 3: Current page is in the middle
      else {
        pageNumbers.push(
          <span
            key="ellipsis1"
            className="flex h-10 w-10 items-center justify-center"
          >
            ...
          </span>
        );
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(
            <button
              key={i}
              onClick={() => handlePageChange(i)}
              disabled={isDisabled || pendingPage === i}
              className={getButtonClassName(i)}
              aria-current={currentPage === i ? "page" : undefined}
             aria-label={`Go to page ${i}`}>
              {i}
            </button>
          );
        }
        pageNumbers.push(
          <span
            key="ellipsis2"
            className="flex h-10 w-10 items-center justify-center"
          >
            ...
          </span>
        );
      }
    }

    // Always show last page if we have more than 1 page
    if (totalPages > 1) {
      pageNumbers.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          disabled={isDisabled || pendingPage === totalPages}
          className={getButtonClassName(totalPages)}
          aria-current={currentPage === totalPages ? "page" : undefined}
         aria-label={`Go to page ${totalPages}`}>
          {totalPages}
        </button>
      );
    }

    return pageNumbers;
  }, [currentPage, getButtonClassName, handlePageChange, isChanging, isLoading, pendingPage, totalPages]);

  const isDisabled = isChanging || isLoading;

  return (
    <nav 
      aria-label="Pagination" 
      className={cn(
        "mx-auto flex w-full justify-center",
        isDisabled && "pointer-events-none opacity-50",
        className
      )}
    >
      <PaginationContent className="flex items-center gap-1">
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1 || isDisabled || pendingPage === currentPage - 1}
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-md text-gray-600 transition-colors",
            (currentPage === 1 || isDisabled || pendingPage === currentPage - 1)
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-gray-100"
          )}
          aria-label="Go to previous page"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>

        {renderPageNumbers()}

        <button
          onClick={handleNext}
          disabled={currentPage === totalPages || isDisabled || pendingPage === currentPage + 1}
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-md text-gray-600 transition-colors",
            (currentPage === totalPages || isDisabled || pendingPage === currentPage + 1)
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-gray-100"
          )}
          aria-label="Go to next page"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </PaginationContent>
    </nav>
  );
};

export {
  PaginationContent,
  PaginationLink,
  PaginationEllipsis,
  PaginationPrevious,
  PaginationNext,
  CustomPagination,
  PaginationItem,
};
